import clsx from 'clsx';
import { Eye, EyeOff, Info, Link as LinkIcon, Lock, Sparkles, Type } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flattenSections, slugify, type DocumentContent, type RichDoc, type Section } from '@imsenta/shared';
import { Badge, Button, Card, Checkbox, Input } from '@/components/ui';
import { CrossReferencePicker, type CrossReferenceValue } from './CrossReferencePicker';
import { RichTextEditor } from './RichTextEditor';
import { SectionTree } from './SectionTree';
import {
  duplicateSection,
  indentSection,
  insertChildSection,
  insertSection,
  moveSection,
  newSection,
  outdentSection,
  removeSection,
  updateSection,
} from './sections';

export interface DocumentEditorProps {
  content: DocumentContent;
  onChange: (content: DocumentContent) => void;
  documentId?: string;
  readOnly?: boolean;
  /** Opens the AI assistant for a specific section. */
  onAssist?: (section: Section) => void;
  aiAvailable?: boolean;
}

/**
 * Structured document authoring.
 *
 * The left rail is the outline (create, reorder and nest sections); the right
 * pane edits one section at a time. Clause numbers are derived from position,
 * so authors never maintain numbering by hand.
 */
export function DocumentEditor({ content, onChange, documentId, readOnly, onAssist, aiAvailable }: DocumentEditorProps) {
  const entries = useMemo(() => flattenSections(content, { includeUnnumbered: true }), [content]);
  const [activeId, setActiveId] = useState<string | null>(entries[0]?.section.id ?? null);
  const [showGuidance, setShowGuidance] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const insertRef = useRef<((value: CrossReferenceValue) => void) | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Keep a valid selection as sections come and go.
  useEffect(() => {
    if (activeId && entries.some((e) => e.section.id === activeId)) return;
    setActiveId(entries[0]?.section.id ?? null);
  }, [entries, activeId]);

  const active = entries.find((e) => e.section.id === activeId) ?? null;

  const select = useCallback((id: string) => {
    setActiveId(id);
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const patchActive = useCallback(
    (patch: Partial<Section>) => {
      if (!activeId) return;
      onChange(updateSection(content, activeId, patch));
    },
    [activeId, content, onChange],
  );

  const handleAdd = (afterId: string | null) => {
    const section = newSection();
    onChange(insertSection(content, afterId, section));
    setActiveId(section.id);
  };

  const handleAddChild = (parentId: string) => {
    const section = newSection('New subsection');
    onChange(insertChildSection(content, parentId, section));
    setActiveId(section.id);
  };

  const handleRemove = (id: string) => {
    const target = entries.find((e) => e.section.id === id);
    if (!target || target.section.locked) return;

    const countDescendants = (section: Section): number =>
      section.children.reduce((total, child) => total + 1 + countDescendants(child), 0);
    const descendants = countDescendants(target.section);
    const hasContent = (target.section.body.content ?? []).some((n) => (n.content?.length ?? 0) > 0);

    if (descendants > 0 || hasContent) {
      const what =
        descendants > 0
          ? `"${target.section.title}" and its ${descendants} subsection${descendants === 1 ? '' : 's'}`
          : `"${target.section.title}" and its content`;
      if (!window.confirm(`Delete ${what}? This cannot be undone from here.`)) return;
    }
    onChange(removeSection(content, id));
  };

  return (
    <div className="grid min-h-0 gap-4 lg:grid-cols-[248px_minmax(0,1fr)] [&>*]:min-w-0">
      <Card className="h-fit min-w-0 lg:sticky lg:top-20 lg:max-h-[calc(100vh-7rem)] lg:overflow-hidden">
        <SectionTree
          content={content}
          activeId={activeId}
          onSelect={select}
          onAdd={handleAdd}
          onAddChild={handleAddChild}
          onMove={(id, direction) => onChange(moveSection(content, id, direction))}
          onIndent={(id) => onChange(indentSection(content, id))}
          onOutdent={(id) => onChange(outdentSection(content, id))}
          onDuplicate={(id) => onChange(duplicateSection(content, id))}
          onRemove={handleRemove}
          readOnly={readOnly}
        />
      </Card>

      <div ref={bodyRef} className="min-w-0">
        {!active ? (
          <Card className="p-10 text-center">
            <p className="text-sm text-ink-500">This document has no sections yet.</p>
            {!readOnly ? (
              <Button variant="primary" className="mt-4" onClick={() => handleAdd(null)}>
                Add the first section
              </Button>
            ) : null}
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="border-b border-ink-200 bg-ink-50/60 px-5 py-4">
              <div className="flex items-center gap-2">
                {active.number ? (
                  <span className="rounded bg-white px-2 py-1 font-mono text-[12px] font-semibold text-ink-500 ring-1 ring-ink-200 ring-inset">
                    {active.number}
                  </span>
                ) : (
                  <Badge tone="neutral">unnumbered</Badge>
                )}
                <Input
                  value={active.section.title}
                  disabled={readOnly || active.section.locked}
                  onChange={(e) => patchActive({ title: e.target.value })}
                  onBlur={(e) => {
                    // Keep the anchor in step with the title until the document is published.
                    if (!active.section.anchor) patchActive({ anchor: slugify(e.target.value) });
                  }}
                  placeholder="Section title"
                  className="flex-1 border-transparent bg-transparent px-2 text-[17px] font-semibold shadow-none hover:border-ink-200 focus:bg-white"
                />
                {active.section.locked ? (
                  <span title="This section comes from the template" className="text-ink-300">
                    <Lock className="size-4" />
                  </span>
                ) : null}
              </div>

              {!readOnly ? (
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <Checkbox
                    label="Numbered clause"
                    checked={active.section.numbered}
                    onChange={(e) => patchActive({ numbered: e.target.checked })}
                  />
                  <Checkbox
                    label="Internal only"
                    hint="Kept out of exports and the catalog"
                    checked={Boolean(active.section.internal)}
                    onChange={(e) => patchActive({ internal: e.target.checked })}
                  />
                  <label className="flex items-center gap-2 text-[13px] text-ink-600">
                    <Type className="size-3.5 text-ink-400" />
                    <span>Anchor</span>
                    <input
                      value={active.section.anchor}
                      onChange={(e) => patchActive({ anchor: slugify(e.target.value) })}
                      className="w-40 rounded border border-ink-200 bg-white px-2 py-0.5 font-mono text-[11.5px] outline-none focus:border-brand-400"
                      title="Stable identifier used by cross references and export links"
                    />
                  </label>
                </div>
              ) : null}
            </div>

            {active.section.guidance && showGuidance ? (
              <div className="flex items-start gap-2.5 border-b border-amber-200 bg-amber-50 px-5 py-3">
                <Info className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <p className="flex-1 text-[13px] text-amber-900">{active.section.guidance}</p>
                <button
                  type="button"
                  onClick={() => setShowGuidance(false)}
                  className="shrink-0 text-amber-600 hover:text-amber-800"
                  title="Hide template guidance"
                >
                  <EyeOff className="size-3.5" />
                </button>
              </div>
            ) : active.section.guidance ? (
              <button
                type="button"
                onClick={() => setShowGuidance(true)}
                className="flex w-full items-center gap-2 border-b border-ink-200 px-5 py-1.5 text-[12px] text-ink-400 hover:text-ink-700"
              >
                <Eye className="size-3.5" /> Show template guidance
              </button>
            ) : null}

            <div className="p-4">
              <RichTextEditor
                key={active.section.id}
                value={active.section.body}
                editable={!readOnly}
                minHeight={260}
                onChange={(body: RichDoc) => patchActive({ body })}
                toolbarExtras={(editor) => (
                  <>
                    <button
                      type="button"
                      title="Insert a cross reference"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        insertRef.current = (value) => {
                          editor.chain().focus().insertCrossReference(value).run();
                        };
                        setPickerOpen(true);
                      }}
                      className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] font-medium text-brand-600 hover:bg-brand-50"
                    >
                      <LinkIcon className="size-3.5" />
                      Cross reference
                    </button>
                    {onAssist && aiAvailable ? (
                      <button
                        type="button"
                        title="Draft this section with AI assistance"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onAssist(active.section)}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] font-medium text-violet-600 hover:bg-violet-50"
                      >
                        <Sparkles className="size-3.5" />
                        AI assist
                      </button>
                    ) : null}
                  </>
                )}
              />
            </div>

            {!readOnly ? (
              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50/60 px-5 py-2.5">
                <span className="text-[12px] text-ink-400">
                  Section {entries.findIndex((e) => e.section.id === active.section.id) + 1} of {entries.length}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleAdd(active.section.id)}>
                    Add section below
                  </Button>
                  <Button size="sm" onClick={() => handleAddChild(active.section.id)}>
                    Add subsection
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        )}
      </div>

      <CrossReferencePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentContent={content}
        currentDocumentId={documentId}
        onInsert={(value) => insertRef.current?.(value)}
      />
    </div>
  );
}

/** Read-only rendering used by previews and the change request view. */
export function DocumentReadView({ content, className }: { content: DocumentContent; className?: string }) {
  const entries = flattenSections(content, { skipInternal: true });
  return (
    <div className={clsx('pf-prose', className)}>
      {entries.map((entry) => {
        const Tag = (`h${Math.min(6, entry.depth + 1)}` as unknown) as keyof JSX.IntrinsicElements;
        return (
          <section key={entry.section.id} id={entry.section.anchor} className="doc-section">
            <Tag>
              {entry.number ? <span className="clause">{entry.number}</span> : null}
              {entry.section.title}
            </Tag>
            <RichTextEditor value={entry.section.body} onChange={() => {}} editable={false} minHeight={0} className="border-0 shadow-none" />
          </section>
        );
      })}
    </div>
  );
}
